// Multi-select filter chips row for the list views. Mirrors the
// Drillcut PlannerListFilters but lighter: status + priority + owner
// for to-dos, status + team for rocks, status + term for issues.
// Returns a state object that views slice by themselves.

import { useState, type ReactNode } from "react";
import { ChevronDown, X } from "lucide-react";
import { DC } from "../sheets/createSlideOver/primitives";

export interface PlannerListFilterState {
  status: string[];
  priority: string[];
  ownerUserIds: string[];
  teamIds: string[];
  watcherOnly?: boolean;
}

export const emptyPlannerFilters: PlannerListFilterState = {
  status: [],
  priority: [],
  ownerUserIds: [],
  teamIds: [],
  watcherOnly: false,
};

interface ChipOption {
  id: string;
  label: string;
}

export function PlannerListFilters({
  state,
  onChange,
  statusOptions,
  priorityOptions,
  extra,
}: {
  state: PlannerListFilterState;
  onChange: (next: PlannerListFilterState) => void;
  statusOptions?: ChipOption[];
  priorityOptions?: ChipOption[];
  extra?: ReactNode;
}) {
  const activeCount =
    state.status.length +
    state.priority.length +
    state.ownerUserIds.length +
    state.teamIds.length +
    (state.watcherOnly ? 1 : 0);

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
        padding: "10px 14px",
        background: DC.surface,
        border: `1px solid ${DC.border}`,
        borderRadius: 8,
      }}
    >
      <div
        style={{
          font: "700 10px/1 var(--bsp-font-sans)",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: DC.grey,
          marginRight: 4,
        }}
      >
        Filters{activeCount > 0 ? ` · ${activeCount}` : ""}
      </div>

      {statusOptions ? (
        <MultiSelectChip
          label="Status"
          options={statusOptions}
          values={state.status}
          onChange={(values) => onChange({ ...state, status: values })}
        />
      ) : null}
      {priorityOptions ? (
        <MultiSelectChip
          label="Priority"
          options={priorityOptions}
          values={state.priority}
          onChange={(values) => onChange({ ...state, priority: values })}
        />
      ) : null}
      {extra}

      {activeCount > 0 ? (
        <button
          type="button"
          onClick={() => onChange(emptyPlannerFilters)}
          style={{
            marginLeft: "auto",
            padding: "4px 8px",
            border: 0,
            background: "transparent",
            color: DC.grey,
            font: "600 11px/1 var(--bsp-font-sans)",
            cursor: "pointer",
          }}
        >
          Clear all
        </button>
      ) : null}
    </div>
  );
}

// Single chip with a built-in popover; multiple values can be toggled.
function MultiSelectChip({
  label,
  options,
  values,
  onChange,
}: {
  label: string;
  options: ChipOption[];
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const summary =
    values.length === 0
      ? label
      : values.length === 1
        ? `${label}: ${options.find((o) => o.id === values[0])?.label ?? values[0]}`
        : `${label}: ${values.length}`;

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          border: `1px solid ${values.length > 0 ? DC.primary : DC.borderStrong}`,
          background: values.length > 0 ? DC.primaryTint : DC.surface,
          color: values.length > 0 ? DC.primary : DC.greyDeep,
          borderRadius: 999,
          font: "600 11px/1 var(--bsp-font-sans)",
          cursor: "pointer",
        }}
      >
        {summary}
        {values.length > 0 ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={`Clear ${label} filter`}
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
            style={{ display: "inline-flex", cursor: "pointer" }}
          >
            <X size={11} />
          </span>
        ) : (
          <ChevronDown size={11} />
        )}
      </button>
      {open ? (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 50 }}
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              minWidth: 180,
              zIndex: 51,
              background: DC.surface,
              border: `1px solid ${DC.borderStrong}`,
              borderRadius: 6,
              boxShadow: "var(--bsp-shadow-pop)",
              padding: 6,
            }}
          >
            {options.map((opt) => {
              const checked = values.includes(opt.id);
              return (
                <label
                  key={opt.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 8px",
                    fontSize: 13,
                    cursor: "pointer",
                    borderRadius: 4,
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLLabelElement).style.background =
                      DC.primaryPale)
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLLabelElement).style.background =
                      "transparent")
                  }
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      if (checked) onChange(values.filter((v) => v !== opt.id));
                      else onChange([...values, opt.id]);
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
